interface RouteOptions {
  hasBottomNav?: boolean;
  hasPageNav?: boolean;
  toastDirection?: 'top' | 'bottom';
}

type DynamicParams = Record<string, string | number | null | boolean>;

type ConditionalDynamicParams<T> = {
  [K in keyof T as T[K] extends undefined ? never : K]: T[K];
} & {
  eventParams?: Record<string, string>;
};

let routeId = 0;

export const createRoute = <
  TDynamicParams extends {
    dynamicPath?: DynamicParams;
    searchParams?: DynamicParams;
  } = {
    dynamicPath: undefined;
    searchParams: undefined;
  },
>(
  pathname: TDynamicParams['dynamicPath'] extends DynamicParams
    ? (dynamicPath: TDynamicParams['dynamicPath']) => string
    : string,
  options?: RouteOptions,
) => {
  routeId += 1;

  return Object.freeze({
    id: routeId,
    pathname,
    isMatchPathname: (currentPathname: string): boolean => {
      if (typeof pathname === 'string') {
        return pathname === currentPathname;
      }

      const routeSegments = pathname({} as DynamicParams).split('/');
      const currentSegments = currentPathname.split('/');

      if (routeSegments.length !== currentSegments.length) {
        return false;
      }

      return currentSegments.every(
        (segment, index) =>
          routeSegments[index] === 'undefined' ||
          segment === routeSegments[index],
      );
    },
    withSearchParams: (
      dynamicParams: ConditionalDynamicParams<TDynamicParams>,
    ) => {
      const params =
        (dynamicParams as unknown as TDynamicParams).searchParams ?? {};
      const eventParams = dynamicParams.eventParams ?? {};
      const searchParams = new URLSearchParams();

      for (const key in params) {
        const value = params[key];

        if (value === undefined) continue;

        searchParams.append(key, String(value));
      }

      for (const key in eventParams) {
        const value = eventParams[key];

        if (value === undefined) continue;

        searchParams.append(key, String(value));
      }

      const search = searchParams.toString();

      const _pathname =
        typeof pathname === 'function'
          ? pathname(
              (dynamicParams as unknown as TDynamicParams).dynamicPath ?? {},
            )
          : pathname;

      return `${_pathname}${search ? `?${search}` : ''}`;
    },
    hasBottomNav: options?.hasBottomNav ?? false,
    hasPageNav: options?.hasPageNav ?? false,
    toastDirection: options?.toastDirection ?? 'bottom',
  } as const);
};
